import { useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Users, GraduationCap, Ticket, Award, Save, ChevronLeft } from "lucide-react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";

export default function Admin() {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <span className="text-xs uppercase tracking-widest font-mono text-[#C47A53]">Owner Panel</span>
      <h1 className="mt-1 font-serif text-3xl sm:text-4xl text-[#1A2621]">Course Administration</h1>

      <Tabs defaultValue="overview" className="mt-8">
        <TabsList className="bg-[#EBE5DC]">
          <TabsTrigger value="overview" data-testid="admin-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="chapters" data-testid="admin-tab-chapters">Chapters</TabsTrigger>
          <TabsTrigger value="tickets" data-testid="admin-tab-tickets">Support</TabsTrigger>
        </TabsList>
        <TabsContent value="overview"><OverviewTab /></TabsContent>
        <TabsContent value="chapters"><ChaptersTab /></TabsContent>
        <TabsContent value="tickets"><TicketsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function OverviewTab() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);
  if (!stats) return <p className="mt-6 text-[#5A6B61]">Loading…</p>;
  const cards = [
    { icon: Users, label: "Students", value: stats.total_students },
    { icon: GraduationCap, label: "Enrolled", value: stats.enrolled_students },
    { icon: Award, label: "Certificates issued", value: stats.certificates_issued },
    { icon: Ticket, label: "Open tickets", value: stats.open_tickets },
  ];
  return (
    <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-5">
      {cards.map((c) => (
        <div key={c.label} className="bg-[#F3EFEA] border border-[#2C4A3E]/12 rounded-2xl p-6">
          <c.icon className="w-6 h-6 text-[#2C4A3E]" />
          <p className="mt-4 font-serif text-3xl text-[#1A2621]">{c.value}</p>
          <p className="text-sm text-[#5A6B61]">{c.label}</p>
        </div>
      ))}
    </div>
  );
}

function ChaptersTab() {
  const [chapters, setChapters] = useState([]);
  const [editing, setEditing] = useState(null);

  useEffect(() => { api.get("/admin/chapters").then(({ data }) => setChapters(data)).catch(() => {}); }, []);

  if (editing) return <ChapterEditor number={editing} onBack={() => setEditing(null)} />;

  return (
    <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {chapters.map((c) => (
        <button key={c.chapter_number} data-testid={`admin-chapter-${c.chapter_number}`} onClick={() => setEditing(c.chapter_number)}
          className="text-left p-4 rounded-xl bg-[#F3EFEA] border border-[#2C4A3E]/12 hover:-translate-y-0.5 hover:shadow-md transition-all">
          <span className="font-mono text-xs text-[#829B88]">Chapter {c.chapter_number}</span>
          <span className="block text-sm font-medium text-[#1A2621] mt-0.5">{c.title}</span>
          <span className="text-xs text-[#5A6B61]">{c.module_title}</span>
        </button>
      ))}
    </div>
  );
}

function ChapterEditor({ number, onBack }) {
  const [ch, setCh] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get(`/admin/chapters/${number}`).then(({ data }) => setCh(data)).catch((e) => toast.error(apiError(e))); }, [number]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/chapters/${number}`, {
        title: ch.title, summary: ch.summary, content: ch.content, quiz: ch.quiz,
      });
      toast.success("Chapter saved.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const updateQuiz = (qi, patch) => setCh((c) => ({ ...c, quiz: c.quiz.map((q, i) => (i === qi ? { ...q, ...patch } : q)) }));
  const updateOption = (qi, oi, val) => setCh((c) => ({ ...c, quiz: c.quiz.map((q, i) => i === qi ? { ...q, options: q.options.map((o, j) => j === oi ? val : o) } : q) }));

  if (!ch) return <p className="mt-6 text-[#5A6B61]">Loading chapter…</p>;

  return (
    <div className="mt-6">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-[#2C4A3E] mb-4" data-testid="admin-editor-back"><ChevronLeft className="w-4 h-4" /> All chapters</button>
      <div className="bg-[#F3EFEA] border border-[#2C4A3E]/12 rounded-2xl p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-[#2C4A3E] mb-1">Title</label>
          <input data-testid="editor-title" value={ch.title} onChange={(e) => setCh({ ...ch, title: e.target.value })}
            className="w-full px-4 py-2.5 rounded-xl border border-[#2C4A3E]/20 bg-white/70 focus:outline-none focus:border-[#2C4A3E]" />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#2C4A3E] mb-1">Summary</label>
          <input data-testid="editor-summary" value={ch.summary || ""} onChange={(e) => setCh({ ...ch, summary: e.target.value })}
            className="w-full px-4 py-2.5 rounded-xl border border-[#2C4A3E]/20 bg-white/70 focus:outline-none focus:border-[#2C4A3E]" />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#2C4A3E] mb-1">Lesson content</label>
          <textarea data-testid="editor-content" rows={12} value={ch.content} onChange={(e) => setCh({ ...ch, content: e.target.value })}
            className="w-full px-4 py-3 rounded-xl border border-[#2C4A3E]/20 bg-white/70 focus:outline-none focus:border-[#2C4A3E] font-light leading-relaxed" />
        </div>
      </div>

      <h3 className="mt-6 font-serif text-xl text-[#1A2621]">Quiz questions</h3>
      <div className="mt-3 space-y-4">
        {ch.quiz.map((q, qi) => (
          <div key={q.id} className="bg-[#F3EFEA] border border-[#2C4A3E]/12 rounded-2xl p-5">
            <label className="block text-sm font-medium text-[#2C4A3E] mb-1">Question {qi + 1}</label>
            <input data-testid={`editor-q-${qi}`} value={q.question} onChange={(e) => updateQuiz(qi, { question: e.target.value })}
              className="w-full px-4 py-2.5 rounded-xl border border-[#2C4A3E]/20 bg-white/70 focus:outline-none focus:border-[#2C4A3E] mb-3" />
            <div className="space-y-2">
              {q.options.map((opt, oi) => (
                <div key={oi} className="flex items-center gap-2">
                  <input type="radio" name={`correct-${qi}`} checked={q.correct_index === oi} onChange={() => updateQuiz(qi, { correct_index: oi })}
                    data-testid={`editor-q-${qi}-correct-${oi}`} className="accent-[#2C4A3E] w-4 h-4" />
                  <input value={opt} onChange={(e) => updateOption(qi, oi, e.target.value)} data-testid={`editor-q-${qi}-opt-${oi}`}
                    className="flex-1 px-3 py-2 rounded-lg border border-[#2C4A3E]/20 bg-white/70 text-sm focus:outline-none focus:border-[#2C4A3E]" />
                </div>
              ))}
            </div>
            <p className="text-xs text-[#5A6B61] mt-2">Select the radio next to the correct answer.</p>
          </div>
        ))}
      </div>

      <button onClick={save} disabled={saving} data-testid="editor-save-btn" className="mt-6 px-8 py-3 rounded-full bg-[#2C4A3E] text-[#FAF8F5] font-semibold hover:bg-[#223B31] flex items-center gap-2 disabled:opacity-60">
        <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save chapter"}
      </button>
    </div>
  );
}

function TicketsTab() {
  const [tickets, setTickets] = useState([]);
  useEffect(() => { api.get("/admin/tickets").then(({ data }) => setTickets(data)).catch(() => {}); }, []);
  if (tickets.length === 0) return <p className="mt-6 text-[#5A6B61]">No support tickets yet.</p>;
  return (
    <div className="mt-6 space-y-3">
      {tickets.map((t) => (
        <div key={t.id} data-testid={`ticket-${t.id}`} className="bg-[#F3EFEA] border border-[#2C4A3E]/12 rounded-2xl p-5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="font-medium text-[#1A2621]">{t.name}</span>
              <span className="text-sm text-[#5A6B61]">· {t.email}</span>
            </div>
            <span className="text-xs font-mono uppercase tracking-wide text-[#C47A53] bg-[#EBE5DC] px-2.5 py-1 rounded-full">{t.category}</span>
          </div>
          <p className="mt-2 text-sm text-[#334139]">{t.message}</p>
          <p className="mt-2 text-xs text-[#829B88]">{new Date(t.created_at).toLocaleString()}</p>
        </div>
      ))}
    </div>
  );
}
